import clsx from "clsx";
import { FC } from "react";
import {
  Decimal,
  LoaderWithContent,
  Progress,
  useTranslation,
} from "rujira.ui";

// Prices carry 12 decimals throughout the app.
const PRICE_DECIMALS = 12;

export const InRange: FC<{
  current: bigint;
  low: bigint;
  high: bigint;
}> = ({ current, low, high }) => {
  if (!current && !low && !high) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  const pct =
    current && high > low
      ? Math.max(
          0,
          Math.min(100, Number(((current - low) * 100n) / (high - low)))
        )
      : null;

  return (
    <div className="in-range">
      <div className="in-range__track">
        {pct !== null && (
          <>
            <span
              className="in-range__price color-teal fs-12"
              style={{ left: `${pct}%` }}>
              <Decimal
                as="span"
                amount={current}
                decimals={PRICE_DECIMALS}
                clip
              />
            </span>
            <div className="in-range-triangle" style={{ left: `${pct}%` }} />
          </>
        )}
        <Progress percentage={100} fillClassName="bg-teal" />
      </div>
      <div className="in-range__limits">
        <span className="in-range__limit-low color-teal fs-12">
          <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
        </span>
        <span className="in-range__limit-high color-teal fs-12">
          <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
        </span>
      </div>
    </div>
  );
};

export const OutOfRange: FC<{
  current: bigint;
  low: bigint;
  high: bigint;
}> = ({ current, low, high }) => {
  const { t } = useTranslation("trade");

  if (!current && !low && !high) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  if (!current && low && high) {
    return <SimpleRange low={low} high={high} />;
  }

  if (!current) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  const isBelow = current < low;
  const rangeToCurrent = isBelow ? high - current : current - low;
  const offset = isBelow
    ? Math.max(15, (Number(low - current) / Number(rangeToCurrent)) * 100)
    : 0;
  const pointerPos = isBelow ? 10 : 85;

  const pct = isBelow
    ? 100 - offset
    : Math.max(
        15,
        Math.min(
          85,
          ((Number(high) - Number(low)) / Number(rangeToCurrent)) * 100
        )
      );

  const pctToReenter =
    Number(((isBelow ? low - current : current - high) * 10000n) / current) /
    100;

  const tooltipHtml = t(isBelow ? "reenterRangeRise" : "reenterRangeFall", {
    pct: `<span class="${isBelow ? "color-teal" : "color-red"}">${isBelow ? "+" : "-"}${pctToReenter.toLocaleDecimal(2)}%</span>`,
  });

  return (
    <div
      className="out-of-range"
      data-tooltip-id="global-tip"
      data-tooltip-html={tooltipHtml}>
      <div className="out-of-range__track">
        <span
          className="out-of-range__price color-orange fs-12"
          style={{ left: `${pointerPos}%` }}>
          <Decimal as="span" amount={current} decimals={PRICE_DECIMALS} clip />
        </span>
        <div
          className="out-of-range-triangle"
          style={{ left: `${pointerPos}%` }}
        />
        <Progress
          percentage={pct}
          fillStart={offset}
          fillClassName="bg-orange"
        />
      </div>
      <div
        className="out-of-range__limits"
        style={{ justifyContent: isBelow ? "flex-end" : "flex-start" }}>
        <span className="color-orange fs-12 flex ai-c gap-0.5">
          <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
          <span>—</span>
          <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
        </span>
      </div>
    </div>
  );
};

// Collapsed-row version of the range bars: no prices, compact mobile view.
export const RangePip: FC<{
  current: bigint;
  low: bigint;
  high: bigint;
  inRange: boolean;
  closed?: boolean;
}> = ({ current, low, high, inRange, closed }) => {
  if (high <= low) return null;

  const tone = closed ? "grey" : inRange ? "teal" : "orange";
  let band = { left: 0, width: 100 };
  let mark: number | null = null;

  if (!current || closed) {
    // No market price to place: just the range itself.
  } else if (inRange) {
    band = { left: 0, width: 100 };
    mark = Math.max(
      0,
      Math.min(100, Number(((current - low) * 10000n) / (high - low)) / 100)
    );
  } else {
    const isBelow = current < low;
    const rangeToCurrent = isBelow ? high - current : current - low;
    const offset = isBelow
      ? Math.max(15, (Number(low - current) / Number(rangeToCurrent)) * 100)
      : 0;
    const width = isBelow
      ? 100 - offset
      : Math.max(
          15,
          Math.min(
            85,
            ((Number(high) - Number(low)) / Number(rangeToCurrent)) * 100
          )
        );

    band = { left: offset, width };
    mark = isBelow ? 10 : 85;
  }

  return (
    <span className={`range-pip range-pip--${tone}`}>
      <span className="range-pip__track">
        <span
          className="range-pip__band"
          style={{ left: `${band.left}%`, width: `${band.width}%` }}
        />
        {mark !== null && (
          <span
            className={clsx("range-pip__mark", {
              "in-range-triangle": inRange,
              "out-of-range-triangle": !inRange,
            })}
            // Inset by the pulsing triangle's half-width so the aside can't clip it
            style={{ left: `clamp(7px, ${mark}%, calc(100% - 7px))` }}
          />
        )}
      </span>
    </span>
  );
};

export const SimpleRange: FC<{
  low: bigint;
  high: bigint;
}> = ({ low, high }) => {
  return (
    <div className="closed-range">
      <span className="color-white fs-12 nowrap">
        <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
      </span>
      <span>-</span>
      <span className="color-white fs-12 nowrap text-right">
        <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
      </span>
    </div>
  );
};
